import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { Box, Typography, Paper, IconButton, Tooltip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useToken } from '../../../hooks/useToken';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import KanbanTicket from './KanbanTicket';
import KanbanColumnPagination from './KanbanColumnPagination';
import EditColumnModal from './EditColumnModal';
import { updateColumn, deleteColumn } from '../../../reducers/kanban/columnActions';
import { loadColumnPage } from '../../../reducers/kanban/ticketActions';

const ColumnContainer = styled(Paper)(({ theme }) => ({
  margin: theme.spacing(0, 0),
  width: 280,
  minWidth: 280,
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100vh - 500px)',
  minHeight: 'calc(100vh - 500px)',
  maxHeight: 'calc(100vh - 500px)',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  flexShrink: 0,
  marginBottom: theme.spacing(0.5),
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
  position: 'relative',
  transition: 'transform 0.2s ease',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
  }
}));

const ColumnHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.grey[100],
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderTopLeftRadius: theme.shape.borderRadius,
  borderTopRightRadius: theme.shape.borderRadius,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const TicketsContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  flexGrow: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  height: 'calc(100vh - 580px)',
  maxHeight: 'calc(100vh - 580px)',
  display: 'flex',
  flexDirection: 'column',
  marginBottom: 'auto',
}));

const DroppableArea = styled(Box)(({ theme, isDraggingOver }) => ({
  padding: theme.spacing(1),
  height: '100%',
  transition: 'background-color 0.2s ease',
  backgroundColor: isDraggingOver ? theme.palette.action.hover : 'transparent',
  overflowY: 'auto',
  overflowX: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const EmptyColumnMessage = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));



/**
 * Componente que representa una columna en el tablero Kanban
 * @param {Object} column - Datos de la columna
 * @param {number} index - Posición de la columna en el tablero
 * @param {Function} onAddTicket - Función para manejar la creación de tickets
 * @param {string} boardType - Tipo de tablero ('general' o 'soporte')
 */
const KanbanColumn = ({ column, index, onAddTicket, boardType = 'general' }) => {
  const dispatch = useDispatch();
  const { getTokenSilent } = useToken();

  /**
   * Normaliza los datos de la columna para evitar errores
   */
  const safeColumn = useMemo(() => {
    if (!column || typeof column !== 'object') {
      return { id: 'invalid-column', name: 'Columna inválida', tickets: [] };
    }

    return {
      ...column,
      id: String(column.id || 'unknown'),
      name: column.name || 'Sin nombre',
      tickets: Array.isArray(column.tickets) ? column.tickets : [],
      board_id: column.board_id || ''
    };
  }, [column]);

  const { tickets: { filters: dateFilters = {}, columnLoadingMore = {}, columnPagination = {} } = { filters: {}, columnLoadingMore: {}, columnPagination: {} } } = useSelector(state => state.kanban);
  const [editColumnModalOpen, setEditColumnModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [enrichedTickets, setEnrichedTickets] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');

  const isLoadingMore = columnLoadingMore[safeColumn.id] || false;
  
  const paginationInfo = columnPagination[safeColumn.id] || {
    currentPage: 1,
    totalTickets: enrichedTickets.length >= 5 ? 10 : enrichedTickets.length,
    hasMore: enrichedTickets.length >= 5
  };
  const isMounted = useRef(true);

  /**
   * Limpia recursos cuando el componente se desmonta
   */
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  /**
   * Actualiza los tickets cuando cambian en la columna
   * Evita actualizaciones innecesarias comparando IDs
   */
  useEffect(() => {
    if (!isMounted.current) return;

    if (safeColumn.tickets && safeColumn.tickets.length > 0) {
      const currentTicketIds = enrichedTickets.map(t => t.id || '').sort().join(',');
      const newTicketIds = safeColumn.tickets.map(t => t.id || '').sort().join(',');

      if (currentTicketIds === newTicketIds && enrichedTickets.length > 0) {
        return;
      }

      if (isMounted.current) {
        setEnrichedTickets(safeColumn.tickets);
      }
    } else if (isMounted.current) {
      setEnrichedTickets([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeColumn.tickets, safeColumn.id]);

  /**
   * Maneja el cambio de página para esta columna
   */
  const handlePageChange = async (newPage) => {
    try {
      const token = await getTokenSilent();
      await dispatch(loadColumnPage(safeColumn.id, newPage, token, dateFilters));
    } catch (error) {
      console.error('❌ Error al cargar más tickets:', error);
    }
  };

  /**
   * Maneja la actualización de la columna desde el modal de edición
   * @param {string|number} columnId - ID de la columna a actualizar
   * @param {Object} columnData - Datos actualizados de la columna
   */
  const handleSaveColumn = async (columnId, columnData) => {
    try {
      await dispatch(updateColumn(columnId, columnData));
    } catch (error) {
      // Error handling
    }
  };

  /**
   * Determina si el arrastre de la columna debe estar deshabilitado
   * - Siempre deshabilitado en tableros de soporte y flujo_bot
   * - Deshabilitado para la columna en posición 1
   */
  const isDragDisabled = useMemo(() => {
    if (boardType === 'soporte' || boardType === 'flujo_bot') {
      return true;
    }

    if (parseInt(safeColumn.position, 10) === 1) {
      return true;
    }

    return false;
  }, [boardType, safeColumn.position]);

  const { columns: { items: allColumns = [] } = { items: [] } } = useSelector(state => state.kanban);
  const { tickets: { columnTickets = {} } = { columnTickets: {} } } = useSelector(state => state.kanban);

  /**
   * Actualiza el board_id de la columna si no está disponible
   */
  useEffect(() => {
    if (!safeColumn.board_id && allColumns && allColumns.length > 0) {
      const matchingColumn = allColumns.find(col => String(col.id) === String(safeColumn.id));
      if (matchingColumn && matchingColumn.board_id) {
        safeColumn.board_id = matchingColumn.board_id;
      }
    }
  }, [safeColumn, allColumns]);

  /**
   * Sincronizar los tickets de la columna con los del mapa global de tickets
   * para asegurarnos de que siempre tenemos los datos más actualizados
   */
  useEffect(() => {
    // Si hay tickets disponibles en el mapa global para esta columna
    if (columnTickets && columnTickets[safeColumn.id]) {
      // Obtener los tickets del mapa global para esta columna
      const globalTickets = columnTickets[safeColumn.id] || [];

      // Verificar si los tickets han cambiado
      const currentTicketIds = enrichedTickets.map(t => t.id).sort().join(',');
      const newTicketIds = globalTickets.map(t => t.id).sort().join(',');

      if (currentTicketIds !== newTicketIds ||
          JSON.stringify(globalTickets) !== JSON.stringify(enrichedTickets)) {

        // Verificar si hay tickets marcados como optimistas en otras columnas
        // para evitar que aparezcan duplicados
        const ticketsOptimisticInOtherColumns = new Set();

        // Buscar en todas las columnas (excepto la actual) tickets con la marca _optimistic
        if (columnTickets) {
          Object.entries(columnTickets).forEach(([colId, colTickets]) => {
            // Solo procesar otras columnas
            if (colId !== String(safeColumn.id) && Array.isArray(colTickets)) {
              colTickets.forEach(ticket => {
                if (ticket && ticket._optimistic) {
                  ticketsOptimisticInOtherColumns.add(String(ticket.id));
                }
              });
            }
          });
        }

        // Filtrar tickets que ya están marcados como optimistas en otras columnas
        const filteredGlobalTickets = globalTickets.filter(
          ticket => !ticketsOptimisticInOtherColumns.has(String(ticket.id))
        );

        if (globalTickets.length !== filteredGlobalTickets.length) {
          // Tickets filtered due to optimistic updates
        }

        safeColumn.tickets = [...filteredGlobalTickets];
        setEnrichedTickets([...filteredGlobalTickets]);
      }
    } else if (enrichedTickets.length > 0 && (!columnTickets || !columnTickets[safeColumn.id] || columnTickets[safeColumn.id].length === 0)) {
      safeColumn.tickets = [];
      setEnrichedTickets([]);
    }
  }, [columnTickets, safeColumn, enrichedTickets]);
  return (
    <>
      {isDragDisabled ? (
        // Para tableros de soporte: sin Draggable wrapper
        <ColumnContainer>
          <ColumnHeader>
              <Typography variant="subtitle1" fontWeight="bold">
                {safeColumn.name}
              </Typography>
              <Box display="flex" alignItems="center">
                {/* Botones de editar y eliminar - solo visibles en tableros generales y no en la primera columna */}
                                    {boardType === 'general' && parseInt(safeColumn.position, 10) !== 1 && (
                      <>
                        <Tooltip title="Editar columna">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => setEditColumnModalOpen(true)}
                              sx={{ mr: 0.5 }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Eliminar columna">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => setDeleteDialogOpen(true)}
                              sx={{ mr: 0.5 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
              </Box>
            </ColumnHeader>

            <TicketsContainer>
              <Droppable
                droppableId={safeColumn.id}
                type="ticket"
              >
                {(provided, snapshot) => (
                  <DroppableArea
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    isDraggingOver={snapshot.isDraggingOver}
                  >
                    {enrichedTickets.length === 0 ? (
                      <EmptyColumnMessage>
                        {(dateFilters.startDate || dateFilters.endDate) ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1 }}>
                            <FilterAltIcon color="primary" sx={{ mb: 1, opacity: 0.7 }} />
                            <Typography variant="body2" color="text.secondary" align="center" fontWeight="medium">
                              Sin resultados para el filtro
                            </Typography>
                            {/* Eliminado el texto de fecha para simplificar la interfaz */}
                          </Box>
                        ) : (
                          <Typography variant="body2">
                            No hay tickets
                          </Typography>
                        )}
                      </EmptyColumnMessage>
                    ) : (
                      enrichedTickets
                        .filter(ticket => ticket.is_active !== false)
                        .map((ticket, idx) => (
                          <KanbanTicket
                            key={ticket.id}
                            ticket={ticket}
                            index={idx}
                            column={{...safeColumn, boardType}}
                          />
                        ))
                    )}
                    {provided.placeholder}
                  </DroppableArea>
                )}
              </Droppable>
            </TicketsContainer>

            {/* Paginador tradicional - AGREGADO TAMBIÉN AL PRIMER BLOQUE */}
            <KanbanColumnPagination
              currentPage={paginationInfo.currentPage}
              totalTickets={paginationInfo.totalTickets}
              ticketsPerPage={5}
              loading={isLoadingMore}
              onPageChange={handlePageChange}
              hasMore={paginationInfo.hasMore}
            />
          </ColumnContainer>
        ) : (
          // Para tableros generales: con Draggable wrapper
          <Draggable
            draggableId={safeColumn.id}
            index={index}
            isDragDisabled={isDragDisabled}
          >
            {(provided, snapshot) => (
              <ColumnContainer
                {...provided.draggableProps}
                ref={provided.innerRef}
                sx={{
                  // Estilos dinámicos durante el drag & drop - más elegantes
                  transform: snapshot.isDragging 
                    ? `${provided.draggableProps.style?.transform} rotate(1deg)` // Menos rotación
                    : provided.draggableProps.style?.transform,
                  transition: snapshot.isDragging ? 'none' : 'transform 0.2s ease',
                  boxShadow: snapshot.isDragging 
                    ? '0 8px 32px rgba(0, 0, 0, 0.15)' // Sombra más sutil
                    : '0 2px 8px rgba(0, 0, 0, 0.1)',
                  opacity: snapshot.isDragging ? 0.98 : 1, // Menos transparencia
                  zIndex: snapshot.isDragging ? 1000 : 'auto',
                  // Anulamos el hover cuando se está arrastrando
                  '&:hover': snapshot.isDragging ? {} : {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  }
                }}
              >
                <ColumnHeader
                  {...provided.dragHandleProps}
                  sx={{
                    // Feedback visual en el header durante arrastre - más sutil
                    backgroundColor: snapshot.isDragging 
                      ? (theme) => theme.palette.grey[200]  // Gris claro en lugar de azul fuerte
                      : (theme) => theme.palette.grey[100],
                    cursor: isDragDisabled ? 'not-allowed' : snapshot.isDragging ? 'grabbing' : 'grab',
                    transition: 'background-color 0.2s ease',
                    // Borde sutil durante el arrastre
                    borderLeft: snapshot.isDragging ? '3px solid #90caf9' : 'none',
                    borderTopLeftRadius: snapshot.isDragging ? '5px' : '4px'
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {safeColumn.name}
                  </Typography>
                  <Box display="flex" alignItems="center">
                    {/* Botones de editar y eliminar - solo visibles en tableros generales y no en la primera columna */}
                    {boardType === 'general' && parseInt(safeColumn.position, 10) !== 1 && (
                      <>
                        <Tooltip title="Editar columna">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => setEditColumnModalOpen(true)}
                              sx={{ mr: 0.5 }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Eliminar columna">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => setDeleteDialogOpen(true)}
                              sx={{ mr: 0.5 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </ColumnHeader>

                <TicketsContainer>
                  <Droppable
                    droppableId={safeColumn.id}
                    type="ticket"
                  >
                    {(provided, snapshot) => (
                      <DroppableArea
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        isDraggingOver={snapshot.isDraggingOver}
                      >
                        {enrichedTickets.length === 0 ? (
                          <EmptyColumnMessage>
                            {(dateFilters.startDate || dateFilters.endDate) ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1 }}>
                                <FilterAltIcon color="primary" sx={{ mb: 1, opacity: 0.7 }} />
                                <Typography variant="body2" color="text.secondary" align="center" fontWeight="medium">
                                  Sin resultados para el filtro
                                </Typography>
                                {/* Eliminado el texto de fecha para simplificar la interfaz */}
                              </Box>
                            ) : (
                              <Typography variant="body2">
                                No hay tickets
                              </Typography>
                            )}
                          </EmptyColumnMessage>
                        ) : (
                          enrichedTickets
                            .filter(ticket => ticket.is_active !== false)
                            .map((ticket, idx) => (
                              <KanbanTicket
                                key={ticket.id}
                                ticket={ticket}
                                index={idx}
                                column={{...safeColumn, boardType}}
                              />
                            ))
                        )}
                        {provided.placeholder}
                      </DroppableArea>
                    )}
                  </Droppable>
                </TicketsContainer>

                {/* Paginador tradicional */}
                <KanbanColumnPagination
                  currentPage={paginationInfo.currentPage}
                  totalTickets={paginationInfo.totalTickets}
                  ticketsPerPage={5}
                  loading={isLoadingMore}
                  onPageChange={handlePageChange}
                  hasMore={paginationInfo.hasMore}
                />
              </ColumnContainer>
            )}
          </Draggable>
        )}

      {/* Modal para editar columna */}
      <EditColumnModal
        open={editColumnModalOpen}
        onClose={() => setEditColumnModalOpen(false)}
        onSave={handleSaveColumn}
        column={safeColumn}
      />

      {/* Diálogo de confirmación para eliminar columna */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-column-dialog-title"
      >
        <DialogTitle id="delete-column-dialog-title">
          Eliminar columna
        </DialogTitle>
        <DialogContent>
          {successMessage ? (
            <Typography color="success.main" fontWeight="bold" align="center" sx={{ py: 2 }}>
              {successMessage}
            </Typography>
          ) : (
            <>
              <DialogContentText>
                ¿Está seguro de que desea eliminar la columna "{safeColumn.name}"?
              </DialogContentText>

              {enrichedTickets.length > 0 && (
                <Typography color="error" fontWeight="bold" sx={{ mt: 1 }}>
                  Esta columna contiene {enrichedTickets.length} ticket(s). No se puede eliminar hasta que esté vacía.
                </Typography>
              )}

              {deleteError && (
                <Typography color="error" sx={{ mt: 1 }}>
                  {deleteError}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            {successMessage ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!successMessage && (
            <Button
              onClick={async () => {
                try {
                  if (enrichedTickets.length > 0) {
                    setDeleteError('No se puede eliminar la columna porque contiene tickets. Mueva los tickets a otra columna primero.');
                    return;
                  }

                  const token = await getTokenSilent();
                  let boardId = safeColumn.board_id;

                  if (!boardId && allColumns && allColumns.length > 0) {
                    const matchingColumn = allColumns.find(col => String(col.id) === String(safeColumn.id));
                    if (matchingColumn && matchingColumn.board_id) {
                      boardId = matchingColumn.board_id;
                    }
                  }

                  const result = await dispatch(deleteColumn(safeColumn.id, token));

                  if (!isMounted.current) return;

                  // Verificar si fue eliminada o ocultada
                  if (result && result.action === 'hidden') {
                    setSuccessMessage(`Columna "${safeColumn.name}" ocultada correctamente (tenía historial)`);
                  } else {
                    setSuccessMessage(`Columna "${safeColumn.name}" eliminada correctamente`);
                  }

                  // Recarga de columnas eliminada - ya no es necesaria después de simplificación

                  if (!isMounted.current) return;

                  setTimeout(() => {
                    if (isMounted.current) {
                      setDeleteDialogOpen(false);
                      setTimeout(() => {
                        if (isMounted.current) {
                          setSuccessMessage('');
                        }
                      }, 300);
                    }
                  }, 1500);
                } catch (error) {
                  if (isMounted.current) {
                    // Extraer mensaje del error del backend (puede estar en error.response.data.detail)
                    let errorMessage = 'Error desconocido';
                    
                    if (error.response && error.response.data && error.response.data.detail) {
                      errorMessage = error.response.data.detail;
                    } else if (error.message) {
                      errorMessage = error.message;
                    }
                    
                    setDeleteError(errorMessage);
                  }
                }
              }}
              color="error"
              disabled={enrichedTickets.length > 0}
            >
              Eliminar
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default KanbanColumn;